import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormattedNumberPipe } from '../../pipes/formatted-number.pipe';
import {
    debounceTime,
    delayWhen,
    map,
    shareReplay,
    startWith,
    tap
} from 'rxjs/operators';
import { combineLatest, interval } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Currency } from '../currency-selector/currency-country-codes';
import { CurrencyFormatStyleType, Part } from '@phensley/cldr';
import { DecimalFormatStyleType } from '@phensley/cldr-core/lib/common/numbers';
import { CLDRService } from '../../services/cldr/cldr.service';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import {
    GlobalValues,
    SettingsValue
} from '../../../features/user-settings/state/user-setting.model';

@Component({
    selector: 'sb-animated-number',
    standalone: true,
    imports: [CommonModule, FormattedNumberPipe],
    templateUrl: './sb-animated-number.component.html',
    styleUrls: ['./sb-animated-number.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbAnimatedNumberComponent {
    @Input() set value(value: number) {
        this._number = !isNaN(value) && isFinite(value) ? value ?? 0 : 0;
        this._number$.next(this._number);
    }

    constructor(
        public userSettingsQuery: UserSettingsQuery,
        private cldrService: CLDRService
    ) {}

    /* temporary fix for safari not supporting counters range https://caniuse.com/mdn-css_at-rules_counter-style_range needed for this animation to work properly */
    isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    private _number: number;

    /** <b>useMonotypeFont</b> - adds "`text-montotype`" class to element to allow using monotype font for component. <p>**Default:** `false`</p> */
    @Input() useMonotypeFont: boolean = false;
    /** <b>minimumFractionDigits</b> - minimum number of decimals to show, 0 equals none. <p>**Default:** `0`</p> */
    @Input() minimumFractionDigits: number;
    /** <b>maximumFractionDigits</b> - maximum number of decimals to show, 0 equals none. Number will be rounded accordingly. <p>**Default:** `4`</p> */
    @Input() maximumFractionDigits: number;
    /** <b>prefix</b> - prefix to show before the number. <p>**Default:** `''`</p> */
    @Input() prefix: string = '';
    /** <b>suffix</b> - suffix to show after the number. <p>**Default:** `''`</p> */
    @Input() suffix: string = '';
    /** <b>formatStyle</b> - style for number. <p>**Default:** `'decimal'`, if ccy is set `'code'`</p> */
    @Input() formatStyle: DecimalFormatStyleType | CurrencyFormatStyleType;
    /** <b>ccy</b> - currency to show. <p>**Default:** `null`</p> */
    @Input() set ccy(ccy: Currency) {
        this._ccy$.next(ccy);
    }

    _number$ = new BehaviorSubject(0);
    _ccy$ = new BehaviorSubject<Currency>(null);

    parts$ = combineLatest([
        this._number$.asObservable().pipe(
            // delay first emit for initial animation
            delayWhen((_, index) => (index === 0 ? interval(100) : interval(0))),
            startWith(0)
        ),
        this._ccy$.asObservable(),
        this.cldrService.cldr$,
        this.userSettingsQuery.selectGlobalSetting$('DIGITS_LARGE'),
        this.userSettingsQuery.selectGlobalSetting$('DIGITS_SMALL'),
        this.userSettingsQuery.selectGlobalSetting$('DIGITS_PERCENT'),
        this.userSettingsQuery.selectGlobalSetting$('CURRENCY_SYMBOL')
    ]).pipe(
        map(
            (
                [
                    number,
                    ccy,
                    cldr,
                    digitsLarge,
                    digitsSmall,
                    digitsPercent,
                    currencySymbol
                ]: [
                    number,
                    Currency,
                    any,
                    SettingsValue<'DIGITS_LARGE'>,
                    SettingsValue<'DIGITS_SMALL'>,
                    SettingsValue<'DIGITS_PERCENT'>,
                    SettingsValue<'CURRENCY_SYMBOL'>
                ],
                index
            ) => {
                // determine number of digits
                const numberOfDigits = (Math.log(Math.abs(number)) * Math.LOG10E + 1) | 0;

                // placeholder for number of digits to show
                let digits;
                let formatStyle = this.formatStyle;

                // use settings config to determine how many decimals to show based on number of digits and format style
                if (formatStyle === 'percent' || formatStyle === 'percent-scaled') {
                    digits = digitsPercent;
                } else if (numberOfDigits <= 3) {
                    digits = digitsSmall;
                } else {
                    digits = digitsLarge;
                }

                // only use short format style if number of digits is greater than 4
                if (index > 0 && formatStyle === 'short' && numberOfDigits <= 4) {
                    // use default format style if short format style is not allowed
                    formatStyle = null;
                }

                // force fractions on first emit (for animation to work) and then use minimumFractionDigits if set, otherwise use settings config
                let minimumFractionDigits =
                    index === 0
                        ? this.minimumFractionDigits ?? 1
                        : this.minimumFractionDigits ?? +digits[0];

                // use maximumFractionDigits if set, otherwise use settings config
                let maximumFractionDigits =
                    index === 0 ? 1 : this.maximumFractionDigits ?? +digits[1];

                // get parts of number, if ccy is set, format as currency, otherwise format as decimal
                let parts: Array<Part & { leadingZeros?: number; id?: number }> = ccy
                    ? cldr.Numbers.formatCurrencyToParts(number, ccy, {
                          group: true,
                          style: (formatStyle as CurrencyFormatStyleType) || 'code',
                          minimumFractionDigits,
                          maximumFractionDigits,
                          symbolWidth:
                              currencySymbol === 'symbol-narrow' ? 'narrow' : 'default'
                      })
                    : cldr.Numbers.formatDecimalToParts(number, {
                          group: true,
                          style: (formatStyle as DecimalFormatStyleType) || 'decimal',
                          minimumFractionDigits,
                          maximumFractionDigits
                      });

                parts = parts.map((part, index) => {
                    // if ccy is set, replace currency part with ccy. TODO: allow symbol instead of code when added to settings
                    if (ccy && part.type === 'currency' && currencySymbol === 'code') {
                        part.value = ccy;
                    }
                    if (part.type === 'fraction' || part.type === 'integer') {
                        part.leadingZeros = part.value.length - (+part.value + '').length;
                    }
                    part.id = index;
                    return part;
                });

                // add prefix and suffix if set
                if (this.prefix) {
                    const prefix = {
                        value: this.prefix,
                        type: 'literal'
                    };
                    parts = [prefix, ...parts];
                }
                if (this.suffix) {
                    const suffix = {
                        value: this.suffix,
                        type: 'literal'
                    };
                    parts = [...parts, suffix];
                }
                return parts;
            }
        ),
        shareReplay(1)
    );

    // fixed number without animation to get correct field size and as fallback for browsers that don't support animating css @property, e.g. Safari.
    fixedNumber$ = this.parts$.pipe(
        map((parts) => parts.map((part) => part.value).join(''))
    );

    trackByIndex(
        index: number,
        item: Part & { leadingZeros?: number; id?: number }
    ): number {
        return item.id;
    }
}
